import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { addYears, startOfDay } from "date-fns";
import styled from "@emotion/styled";
import truncate from "lodash/truncate";
import { CircularProgress } from "@mui/material";
import { useAccountsId } from "@bn/api/apiv1";
import UpdateIcon from "@icons/myicons/refresh.svg?react";
import AddIcon from "@icons/myicons/plus.svg?react";
import { toCurrency } from "@helpers";
import { MultibankingApiProvider } from "@api";
import { useBankImportReturnUrl, useRoutes } from "@hooks";
import {
  AccountChart,
  PrimaryActionWithMenu,
  ConfirmDeleteDialog,
  AccountsHeader,
  CardPageLayout,
  CardHeading,
} from "@components";
import AccountPreviewIcon from "@components/Accounts/AccountPreviewCard/AccountPreviewIcon";
import { useDeleteAccount, useUpdateAccount } from "@api/v1/hooks/useAccounts";
import { getStartDateForTransactions } from "@helpers/getStartDateForTransactions.tsx";
import { SingleAccountMenu } from "./components/SingleAccountMenu";
import { useAccountChartData } from "./hooks/useAccountChartData";
import { AccountProtocolTable } from "./components/AccountProtocolTable";

interface Props {
  accountId: number;
}

const StyledAccountChart = styled(AccountChart)`
  min-height: 357px;
`;

const TableWrapper = styled.div`
  margin: ${({ theme }) => theme.spacing(1, 0, 3)};
  width: 100%;
`;

export const SingleAccountPage = ({ accountId }: Props) => {
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { saveReturnUrl } = useBankImportReturnUrl();
  const { pushUpdateConnection } = useRoutes();

  const startDate = startOfDay(addYears(new Date(), -1));

  const [chartStartDate, setChartStartDate] = useState<Date | undefined>(
    startDate
  );

  const [chartEndDate, setChartEndDate] = useState<Date | undefined>(
    new Date()
  );

  const { data: account } = useAccountsId({
    id: accountId,
  });

  const {
    data: chartData,
    isPending,
    isFetching,
    timeBrushData,
  } = useAccountChartData({
    accountId,
    startDate: chartStartDate,
    endDate: getStartDateForTransactions(chartEndDate),
  });

  const handleUpdate = async () => {
    if (!account?.bankConnectionId || isUpdating) return;
    setIsUpdating(true);
    try {
      saveReturnUrl();
      await pushUpdateConnection(
        account.bankConnectionId,
        account.bankConnectionType as MultibankingApiProvider
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const {
    mutateAsync,
    reset: resetDeleteAccountResult,
    isPending: isDeleteAccountLoading,
    isError: isDeleteAccountError,
    error: deleteAccountError,
  } = useDeleteAccount();

  const { mutateAsync: updateMutateAsync } = useUpdateAccount();

  const handleDeleteAccount = async () => {
    await mutateAsync(accountId);
    navigate("/accounts");
  };

  const handleHideAccount = async () => {
    await updateMutateAsync({
      id: accountId,
      isHidden: !account!.isHidden,
      type: account!.type!,
      name: account!.name,
    });
    navigate("/accounts");
  };

  const handleRangeSelect = (type: IntervalType, value?: RangeType) => {
    setChartStartDate(value?.startDate);
    setChartEndDate(value?.endDate);
  };

  const handleOpenDeleteModal = () => {
    setDeleteModalShow(true);
  };
  const handleCloseDeleteModal = () => {
    setDeleteModalShow(false);

    if (isDeleteAccountError) {
      resetDeleteAccountResult();
    }
  };

  const shouldDisplayUpdateButton =
    Boolean(account?.bankConnectionType) &&
    account?.bankConnectionType !== "NETFONDS";

  return (
    <CardPageLayout
      breadcrumbs={[
        { to: "/", text: t("breadcrumbs.home") },
        {
          to: "/depots-accounts",
          text: t("breadcrumbs.depotsAccounts"),
        },
        { text: truncate(account?.name) },
      ]}
      cardHeading={
        <CardHeading
          title={
            <AccountsHeader
              title={account?.name ?? ""}
              subtitle={account?.bankName ?? ""}
              icon={
                account ? (
                  <AccountPreviewIcon
                    isReference={Boolean(account.depotIds?.length ?? 0)}
                    iconUrl={account.iconUrl}
                    type={account.type}
                    hideRefIcon
                    size={48}
                  />
                ) : (
                  <></>
                )
              }
            />
          }
          actions={
            <PrimaryActionWithMenu
              startIcon={
                isUpdating ? (
                  <CircularProgress size={18} color="inherit" />
                ) : shouldDisplayUpdateButton ? (
                  <UpdateIcon height="22px" width="22px" />
                ) : (
                  <AddIcon width="18px" height="12px" />
                )
              }
              primaryActionText={t(
                shouldDisplayUpdateButton
                  ? "singleAccountPage.header.update"
                  : "singleAccountPage.header.addTransaction"
              )}
              primaryActionOnClick={
                shouldDisplayUpdateButton ? handleUpdate : undefined
              }
              primaryActionUrl={
                shouldDisplayUpdateButton
                  ? undefined
                  : `/accounts/${account?.id}/transactions/add-manually`
              }
              primaryActionDisabled={isUpdating}
            >
              <SingleAccountMenu
                account={account!}
                handleOpenDeleteModal={handleOpenDeleteModal}
                handleHideAccount={handleHideAccount}
              />
            </PrimaryActionWithMenu>
          }
          actionsInline
        />
      }
      cardChildren={
        <StyledAccountChart
          timeBrushData={timeBrushData}
          isFetching={isPending || isFetching}
          title={toCurrency(account?.balance ?? 0, account?.currency ?? "EUR")}
          data={chartData}
          onSelect={handleRangeSelect}
        />
      }
    >
      <ConfirmDeleteDialog
        isOpen={deleteModalShow}
        title={t("depots.deleteModal.question")}
        isDeleteRequestLoading={isDeleteAccountLoading}
        errorMessage={deleteAccountError?.response?.data?.message}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteAccount}
      />

      <TableWrapper>
        <AccountProtocolTable
          summaryValue={account?.balance ?? 0}
          accountId={accountId}
        />
      </TableWrapper>
    </CardPageLayout>
  );
};
